import { ErrorRow } from '@folio/components/module-elements/ErrorRow';
import { LoadingRow } from '@folio/components/module-elements/LoadingRow';
import { usePolicyOnDashboard } from '@folio/utils/usePolicyOnDashboard';
import {
  AppleStoreLogo,
  Blockquote,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  GooglePlayLogo,
  LoadingCard,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  formatDate,
  getLocaleMonth,
} from '@korint/folio';
import { useQuery } from '@tanstack/react-query';
import {
  axeceControllerListDrivequantAverageScoresQueryOptions,
  customerControllerGetCustomerQueryOptions,
  keyValueFieldsToObject,
} from 'korint-sdk';
import { useTranslation } from 'react-i18next';

export const PolicyholderDrivequant = () => {
  const { i18n, t } = useTranslation();
  const { policy, workingBranch } = usePolicyOnDashboard();

  const policyId = policy.id;
  const customerId = policy.customers?.[0];

  const { data: customer, isLoading: isCustomerLoading } = useQuery({
    ...customerControllerGetCustomerQueryOptions(customerId!, { branchId: workingBranch?.id }),
    enabled: !!customerId,
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const {
    data: { scores } = {},
    isLoading: isListScoresLoading,
    status,
  } = useQuery({
    ...axeceControllerListDrivequantAverageScoresQueryOptions(policyId),
    enabled: !!policyId,
  });
  const drivequantId = customer?.customFields.driveQuantIdConnect;

  const isLoading = isCustomerLoading || isListScoresLoading;
  if (isLoading) {
    return <LoadingCard />;
  }

  if (!drivequantId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('drivequant.account')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p>{t('drivequant.notLinked')}</p>
        </CardContent>
      </Card>
    );
  }

  if (status === 'error') {
    return <ErrorRow />;
  }

  if (status === 'pending') {
    return <LoadingRow />;
  }

  if (!scores) {
    return <ErrorRow>{t('drivequant.driver.noScoresFound')}</ErrorRow>;
  }

  scores.sort(
    (a, b) => new Date(b.period.endedAt).getTime() - new Date(a.period.endedAt).getTime(),
  );
  return (
    <div className="space-y-8">
      <Card>
        <CardHeader>
          <CardTitle>{t('drivequant.driver.historyTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {scores.length > 0 ? (
            <Table className="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('drivequant.history.repricedOn')}</TableHead>
                  <TableHead>{t('drivequant.users.columnHeader')}</TableHead>
                  <TableHead>{t('drivequant.history.periods')}</TableHead>
                  <TableHead className="text-right">{t('drivequant.history.scores')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {scores.flatMap(({ score, users, period }) => {
                  const rows = users.length > 0 ? users : [undefined];
                  return rows.map((user, idx) => (
                    <TableRow key={`${period.endedAt}-${user?.externalServiceUserId ?? idx}`}>
                      {idx === 0 ? (
                        <TableCell className="font-medium" rowSpan={rows.length}>
                          {formatDate(new Date(period.endedAt))}
                        </TableCell>
                      ) : null}
                      <TableCell>
                        {user ? (user.cognitoEmail ?? user.externalServiceUserId) : '—'}
                      </TableCell>
                      <TableCell>
                        {(user?.datesScored ?? [])
                          .map(date => getLocaleMonth(new Date(date), i18n.language))
                          .join(' - ')}
                      </TableCell>
                      {idx === 0 ? (
                        <TableCell className="text-right" rowSpan={rows.length}>
                          {score ?? t('drivequant.history.notEnoughScores')}
                        </TableCell>
                      ) : null}
                    </TableRow>
                  ));
                })}
              </TableBody>
            </Table>
          ) : (
            <p>{t('drivequant.driver.historyUnavailable')}</p>
          )}
          <Blockquote className="mt-2 bg-blue-100 border-blue-400">
            {t('drivequant.driver.appUsageNotice')}
            <div className="mt-4 flex gap-2">
              <a
                href={'https://play.google.com/store/apps/details?id=com.axece.axece'}
                target="_blank"
                rel="noreferrer"
              >
                <GooglePlayLogo />
              </a>
              <a
                href="https://apps.apple.com/us/app/axece/id6747380021"
                target="_blank"
                rel="noreferrer"
              >
                <AppleStoreLogo />
              </a>
            </div>
          </Blockquote>
        </CardContent>
      </Card>
    </div>
  );
};
